/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@nextiva/next-ui-components';
import { Controller } from 'react-hook-form-new';
import { useGetUseCases } from 'hooks/SMS/useGetUseCases';
import { isDisabled } from 'utils/isDisabled';
import {
  CHECKBOX_STYLE,
  DESCRIPTION_MARGIN_STYLE,
  MESSAGING_PLAN_FIELDS,
} from '../../constants';
import { NewRegistrationDialog } from '../RegistrationDialog/RegistrationDialog';
import { useWizardContextV3 } from '../../WizardContextV3/WizardContextV3';
import { useGetSMSCampaignInfo } from '../../WizardContextV3/useGetSMSCampaignInfo';

const dataTestId = 'PlatformAdminUI-Messaging-SmsUseCases';
export const SmsSubUseCases = () => {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const {
    control,
    formState: { errors },
    isDeclined,
    setPrevSubUseCases,
    prevSubUseCases,
    getValues,
  } = useWizardContextV3();
  const { data: useCases, isLoading: isLoadingUseCases } = useGetUseCases();
  const {
    data: smsCampaignInfo,
    isLoading: isLoadingSmsCampaignInfo,
  } = useGetSMSCampaignInfo();

  const campaignId = !!smsCampaignInfo?.data[0]?.campaignId;
  const showEditSelection =
    isDeclined && prevSubUseCases === null && campaignId;

  const handleCreateNewRegistration = () => {
    setPrevSubUseCases(String(getValues(MESSAGING_PLAN_FIELDS.SUB_USE_CASES)));
    setOpenDialog(false);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldParam: {
      name: string;
      onChange: (value: string) => void;
      value: string;
    },
  ) => {
    if (
      fieldParam.value === null ||
      fieldParam.value === undefined ||
      fieldParam.value === ''
    ) {
      fieldParam.onChange(event.target.name);
    } else if (event.target.checked) {
      fieldParam.onChange(`${fieldParam.value},${event.target.name}`);
    } else {
      fieldParam.onChange(
        fieldParam.value
          .split(',')
          .filter((item) => item !== event.target.name)
          .join(','),
      );
    }
  };

  return (
    <div>
      <Divider />
      <Stack
        direction="row"
        alignItems="flex-end"
        justifyContent="space-between"
        mt={4}
        mb={2}
      >
        <Typography variant="subtitle2" color="#0B1029" fontWeight={500}>
          Which use case(s) match your text messaging activity?
        </Typography>
        {showEditSelection && !isLoadingSmsCampaignInfo && (
          <Button
            variant="outlined"
            color="primary"
            size="small"
            onClick={() => setOpenDialog(true)}
            data-testid={`${dataTestId}-EditSelectionButton`}
            sx={{
              '& .MuiButton-sizeSmall': {
                padding: '4px 8px',
              },
            }}
          >
            Edit selection
          </Button>
        )}
      </Stack>
      {!isDeclined ||
        (prevSubUseCases !== null && (
          <Alert
            severity="info"
            fullWidth
            data-testid={`${dataTestId}-InfoAlert`}
            sx={{ mb: 2 }}
          >
            Once your business messaging registration is submitted, changes to
            the use case(s) are not permitted. To update the use case later, a
            new registration must be submitted, which will incur minimum of 3
            months of recurring fees. Please review all use case details
            carefully before submitting to avoid unnecessary charges.
          </Alert>
        ))}
      <Typography
        variant="body2"
        mb={2}
        sx={{ color: errors.subUseCases ? 'error.dark' : '#55596B' }}
      >
        Select all that applies (Minimum 1, Maximum 5) *
      </Typography>
      {!isLoadingUseCases && (
        <Stack direction="row" spacing={4} data-testid={dataTestId}>
          <FormControl required component="fieldset">
            <FormGroup>
              <Controller
                name={MESSAGING_PLAN_FIELDS.SUB_USE_CASES}
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const fieldValue =
                    typeof field.value === 'string'
                      ? field.value
                      : String(field.value ?? '');
                  const selectedCount = fieldValue
                    ? fieldValue.split(',').length
                    : 0;
                  return (
                    <>
                      {Object.entries(useCases?.firstUseCases).map(
                        ([key, value]) => (
                          <Stack
                            key={key}
                            data-testid={`${dataTestId}-${value.displayName}-Checkbox`}
                          >
                            <FormControlLabel
                              label={
                                <Typography
                                  variant="body2"
                                  color="#0B1029"
                                  fontWeight={800}
                                  ml={1}
                                >
                                  {value.displayName}
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  sx={CHECKBOX_STYLE}
                                  checked={
                                    field.value === null ||
                                    field.value === undefined ||
                                    field.value === ''
                                      ? false
                                      : fieldValue.split(',').includes(key)
                                  }
                                  data-testid={`${dataTestId}-${value.displayName}-Checkbox`}
                                  disabled={isDisabled(
                                    key,
                                    fieldValue,
                                    selectedCount,
                                    isDeclined,
                                    prevSubUseCases !== null,
                                    campaignId,
                                  )}
                                  name={key}
                                  onChange={(e) => {
                                    handleChange(e, {
                                      name: field.name,
                                      onChange: field.onChange,
                                      value: fieldValue,
                                    });
                                  }}
                                />
                              }
                            />
                            <Typography
                              variant="caption"
                              color="#616576"
                              sx={DESCRIPTION_MARGIN_STYLE}
                            >
                              {value.description}
                            </Typography>
                          </Stack>
                        ),
                      )}
                    </>
                  );
                }}
              />
            </FormGroup>
          </FormControl>
          <FormControl required component="fieldset">
            <FormGroup>
              <Controller
                name={MESSAGING_PLAN_FIELDS.SUB_USE_CASES}
                control={control}
                rules={{ required: true }}
                render={({ field }) => {
                  const fieldValue =
                    typeof field.value === 'string'
                      ? field.value
                      : String(field.value ?? '');
                  const selectedCount = fieldValue
                    ? fieldValue.split(',').length
                    : 0;
                  return (
                    <>
                      {Object.entries(useCases?.lastUseCases).map(
                        ([key, value]) => (
                          <Stack
                            key={key}
                            data-testid={`${dataTestId}-${value.displayName}-Checkbox`}
                          >
                            <FormControlLabel
                              label={
                                <Typography
                                  variant="body2"
                                  color="#0B1029"
                                  fontWeight={800}
                                  ml={1}
                                >
                                  {value.displayName}
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  sx={CHECKBOX_STYLE}
                                  checked={
                                    field.value === null ||
                                    field.value === undefined ||
                                    field.value === ''
                                      ? false
                                      : fieldValue.split(',').includes(key)
                                  }
                                  data-testid={`${dataTestId}-${value.displayName}-Checkbox`}
                                  disabled={isDisabled(
                                    key,
                                    fieldValue,
                                    selectedCount,
                                    isDeclined,
                                    prevSubUseCases !== null,
                                    campaignId,
                                  )}
                                  name={key}
                                  onChange={(e) => {
                                    handleChange(e, {
                                      name: field.name,
                                      onChange: field.onChange,
                                      value: fieldValue,
                                    });
                                  }}
                                />
                              }
                            />
                            <Typography
                              variant="caption"
                              color="#616576"
                              sx={DESCRIPTION_MARGIN_STYLE}
                            >
                              {value.description}
                            </Typography>
                          </Stack>
                        ),
                      )}
                    </>
                  );
                }}
              />
            </FormGroup>
          </FormControl>
        </Stack>
      )}
      <NewRegistrationDialog
        handleButton1={() => setOpenDialog(false)}
        handleButton2={handleCreateNewRegistration}
        open={openDialog}
      />
    </div>
  );
};
